import type { ComponentPropsWithoutRef } from 'react';
import { useMutation, UseMutationResult, useQueryClient } from 'react-query';
import { authToken, userRegister } from '@simplywallst/services';
import type { Apple } from '@components/OAuthButtons';
import { useDispatch } from 'react-redux';
import { login, registration } from '@/redux/auth/routines';
import { setAuthCookie, convertExpiresInToExpiresAt } from '@utilities/cookies';
import { queryCompanyViewKey } from '@/hooks/useCompanyLimits/queries';
import { queryKey as userAccessQueryKey } from '@/hooks/useUserAccess';
import produce from 'immer';
import { SWS_CLIENT_ID, AUTH_SCOPE } from '@/constants/env';
import {
  QUERY_KEY as USER_KEY,
  ProfileState,
  getDefaultProfileState,
} from '@/hooks/useUser';
import { countries, CountryISO } from '@/utilities/countries';
import type { ISO2, CurrencyISO } from '@/utilities/countries';
import { handleApiError } from '@/utilities/errorHandler';
import { usePreloadUserpilot } from '@/pages/Company/hooks/useUserpilot';
import { useCoupon } from '@/hooks/useCoupon';

const getCurrencyISO = (countryISO: ISO2) => {
  const currency: CurrencyISO = 'USD';
  if (typeof countryISO === 'undefined') return currency;
  const mappedCurrency = countries[CountryISO[countryISO]].currency;
  if (typeof mappedCurrency === 'undefined') return currency;
  return mappedCurrency;
};

type AuthPayload = Parameters<typeof authToken>[0];

enum AuthType {
  SWS = 'sws',
  Apple = 'Apple',
  Facebook = 'Facebook',
  Google = 'Google',
}

type SignInParams = {
  type: AuthType;
  token?: string;
  user?: Parameters<ComponentPropsWithoutRef<typeof Apple>['onSuccess']>[1];
  username?: string;
  password?: string;
  referralData?: string;
};

export function useLogin({
  onSuccess = () => undefined,
  onError,
}: {
  onSuccess?: (
    token: Awaited<ReturnType<typeof authToken>> & { provider: AuthType }
  ) => void;
  onError?: (error: unknown) => void;
}): UseMutationResult<
  Awaited<ReturnType<typeof authToken>> & { provider: AuthType },
  string,
  SignInParams,
  unknown
> {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { registerCoupon } = useCoupon();
  return useMutation(
    async (params) => {
      console.log('useLogin', { params });
      try {
        const authPayload: Partial<AuthPayload> = {
          client_id: SWS_CLIENT_ID,
          cross_client: IS_NATIVE,
          provider: params.type,
          scope: AUTH_SCOPE,
          source: 'react',
        };
        if (
          [AuthType.Apple, AuthType.Facebook, AuthType.Google].includes(
            params.type
          )
        ) {
          /** append social params */
          authPayload['grant_type'] = 'social';
          if (typeof params.token === 'string') {
            authPayload['token'] = params.token;
          }
          if (typeof params.user !== 'undefined') {
            authPayload['user'] = JSON.stringify(params.user);
          }
          authPayload['referral_data'] = params.referralData;
        }
        if (params.type === AuthType.SWS) {
          authPayload['grant_type'] = 'password';
          authPayload['password'] = params.password;
          authPayload['username'] = params.username;
        }
        const authTokenResponse = await authToken(
          authPayload as Required<AuthPayload>
        );

        return { ...authTokenResponse, provider: params.type };
      } catch (error: any) {
        if (typeof error.response !== 'undefined') {
          switch (error.response.data.error_description.toLowerCase()) {
            case 'user account already exists: facebook':
              throw 'Account is accessible via Facebook sign in';
            case 'user account already exists: google':
              throw 'Account is accessible via Google sign in';
            case 'user account already exists: apple':
              throw 'Account is accessible via Apple sign in';
            case 'user account already exists: local':
              throw 'Account is accessible via Email sign in';
            default:
              throw error.response.data.error_description;
          }
        }
        throw error;
      }
    },
    {
      onSuccess: async (authTokenResponse) => {
        console.log('useLogin:success', { authTokenResponse });
        const { access_token, refresh_token, expires_in } =
          authTokenResponse.data;
        try {
          /** set auth cookie so withAuth can function */
          const expiresAt = convertExpiresInToExpiresAt(expires_in);
          setAuthCookie(access_token, refresh_token, expiresAt);
        } catch (error) {
          console.error(error);
        }

        dispatch(
          login.success({
            tokens: authTokenResponse.data,
            provider: authTokenResponse.provider,
          })
        );

        /** register any coupon from persistent storage upon login */
        registerCoupon();

        /** clear uac and limits */
        queryClient.refetchQueries(queryCompanyViewKey);
        queryClient.refetchQueries(userAccessQueryKey);
        queryClient.refetchQueries(USER_KEY);
        /** */

        // User is fully logged in **and** all critical User updates have processed
        onSuccess(authTokenResponse);
      },
      onError(error) {
        if (typeof onError === 'function') {
          onError(error);
        }
      },
    }
  );
}

type CreateAccountParams = {
  email: string;
  password: string;
  firstName: string;
  referralData?: string;
};

export function useRegister({
  onSuccess = () => undefined,
  onError,
}: {
  onSuccess?: (response: Awaited<ReturnType<typeof userRegister>>) => void;
  onError?: (error: unknown) => void;
}): UseMutationResult<
  Awaited<ReturnType<typeof userRegister>>,
  string,
  CreateAccountParams,
  unknown
> {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const preloadUserPilot = usePreloadUserpilot();
  const { registerCoupon } = useCoupon();
  return useMutation(
    async (params) => {
      console.log('useRegister', { params });
      try {
        const response = await userRegister({
          client_id: SWS_CLIENT_ID,
          email: params.email,
          name: params.firstName,
          password: params.password,
          scope: AUTH_SCOPE,
          source: 'react',
          referral_data: params.referralData,
        });
        return response;
      } catch (error) {
        throw handleApiError(error);
      }
    },
    {
      onMutate: preloadUserPilot,
      onSuccess: async (response) => {
        console.log('useRegister:success', { response });

        const responseData = response.data.data;
        const [responseToken] = responseData.token;
        try {
          const { access_token, refresh_token, expires_in } = responseToken;
          const expiresAt = convertExpiresInToExpiresAt(expires_in);
          setAuthCookie(access_token, refresh_token, expiresAt);
        } catch (e) {
          console.error(e);
        }

        /** Preset some user data prior to registration transition */
        queryClient.setQueryData<ProfileState>(
          USER_KEY,
          (state = getDefaultProfileState()) =>
            produce(state, (draft) => {
              draft.emailAddress = responseData.email;
              draft.countryISO = responseData.country_iso;
              return draft;
            })
        );

        /** dispatch a redux action */
        dispatch(
          registration.success({
            currencyISO: getCurrencyISO(responseData.country_iso as ISO2),
            tokens: {
              ...responseToken,
              created: true,
            },
          })
        );

        /** register any coupon from persistent storage upon registration */
        registerCoupon();

        /** clear uac cache */
        queryClient.refetchQueries(userAccessQueryKey);
        queryClient.refetchQueries(USER_KEY);

        // User is fully registered **and** all critical User updates have processed
        onSuccess(response);
      },
      onError(error) {
        if (typeof onError === 'function') {
          onError(error);
        }
      },
    }
  );
}
